import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  ShieldCheck,
  Eye,
  EyeSlash,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

const AlterarSenhaTemporaria = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('A nova senha deve possuir pelo menos 8 caracteres.');
      return;
    }

    if (newPassword !== confirmation) {
      setError('A confirmação da senha não confere.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/auth/change-temporary-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { withCredentials: true }
      );

      await checkAuth();
      navigate('/meu-perfil', { replace: true });
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
        'Não foi possível alterar a senha.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
        <div className="p-7 bg-indigo-600 text-white">
          <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center mb-5">
            <ShieldCheck size={32} weight="duotone" />
          </div>

          <h1 className="text-3xl font-bold">
            Crie sua senha definitiva
          </h1>

          <p className="text-indigo-100 mt-2">
            Por segurança, a senha temporária só pode ser usada no primeiro acesso.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-7 space-y-5">
          {error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <PasswordField
            label="Senha temporária"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showPassword}
          />

          <PasswordField
            label="Nova senha"
            value={newPassword}
            onChange={setNewPassword}
            show={showPassword}
          />

          <PasswordField
            label="Confirmar nova senha"
            value={confirmation}
            onChange={setConfirmation}
            show={showPassword}
          />

          <label className="flex items-center gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(event) =>
                setShowPassword(event.target.checked)
              }
              className="w-4 h-4"
            />

            {showPassword ? (
              <EyeSlash size={18} />
            ) : (
              <Eye size={18} />
            )}

            Exibir senhas
          </label>

          <div className="p-4 rounded-xl bg-slate-50 text-sm text-slate-600">
            Use pelo menos 8 caracteres. Evite senhas óbvias ou já usadas
            em outros serviços.
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            <Lock size={20} />
            {loading
              ? 'Salvando...'
              : 'Criar senha e continuar'}
          </button>
        </form>
      </div>
    </div>
  );
};

const PasswordField = ({
  label,
  value,
  onChange,
  show,
}) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label}
    </label>

    <div className="relative">
      <Lock
        size={20}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      />

      <input
        required
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-slate-300 pl-10 pr-4 py-3"
      />
    </div>
  </div>
);

export default AlterarSenhaTemporaria;
